import React from 'react';
import Link from 'next/link';
import algoliasearch from 'algoliasearch/lite';

import {
  InstantSearch,
  Configure,
  Hits,
  Highlight,
  connectPagination,
  connectSearchBox,
} from 'react-instantsearch-dom';

const searchClient = algoliasearch(
  'FZ1UYTZKMS',
  'f76e6ce47a620af38e01f0be6b881c3e'
);
interface CustomPaginationProps {
  currentRefinement: number;
  nbPages: number;
  refine: (value: number) => void;
}
const CustomPagination = ({
  currentRefinement,
  nbPages,
  refine,
}: CustomPaginationProps) => {
  const isFirstPage = currentRefinement === 1;
  const isLastPage = currentRefinement === nbPages;

  const handlePreviousClick = () => {
    refine(currentRefinement - 1);
  };

  const handleNextClick = () => {
    refine(currentRefinement + 1);
  };

  return (
    <div className="flex justify-center my-8">
      <button
        className={`bg-gray-200 hover:bg-gray-300 text-gray-700 font-bold py-2 px-4 rounded-l ${
          isFirstPage ? 'opacity-50 cursor-not-allowed' : ''
        }`}
        onClick={handlePreviousClick}
        disabled={isFirstPage}
      >
        Previous
      </button>
      <div className="bg-gray-200 text-gray-700 font-bold py-2 px-4">
        Page {currentRefinement} of {nbPages}
      </div>
      <button
        className={`bg-gray-200 hover:bg-gray-300 text-gray-700 font-bold py-2 px-4 rounded-r ${
          isLastPage ? 'opacity-50 cursor-not-allowed' : ''
        }`}
        onClick={handleNextClick}
        disabled={isLastPage}
      >
        Next
      </button>
    </div>
  );
};

const ConnectedPagination = connectPagination(CustomPagination);

interface CustomSearchBoxProps {
  currentRefinement: string;
  refine: (value: string) => void;
  setSearchTerm: (value: string) => void;
}

const CustomSearchBox = ({
  currentRefinement,
  refine,
  setSearchTerm,
}: CustomSearchBoxProps) => {
  const [searchTerm, setLocalSearchTerm] = React.useState(currentRefinement);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newSearchTerm = event.currentTarget.value;
    refine(newSearchTerm);
    setLocalSearchTerm(newSearchTerm);
    setSearchTerm(newSearchTerm); // update the search term state in the parent component
  };

  return (
    <div className="relative">
      <input
        type="search"
        value={searchTerm}
        onChange={handleChange}
        className="w-full h-12 pl-10 pr-4 text-gray-700 placeholder-gray-500 border-b-2 border-gray-400 focus:outline-none focus:border-blue-500"
        placeholder="Search Solar Installers"
      />
      <svg
        className="absolute left-3 top-3 h-5 w-5 text-gray-500"
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <circle cx="11" cy="11" r="8" />
        <line x1="21" y1="21" x2="16.65" y2="16.65" />
      </svg>
      {searchTerm && (
        <button
          onClick={() => {
            refine('');
            setLocalSearchTerm('');
            setSearchTerm(''); // update the search term state in the parent component
          }}
          className="absolute right-3 top-3 h-4 w-4 text-gray-500 focus:outline-none"
        ></button>
      )}
    </div>
  );
};

const ConnectedCustomSearchBox = connectSearchBox(CustomSearchBox);

function ReviewSearch() {
  const [searchTerm, setSearchTerm] = React.useState('');

  return (
    <div>
      <div className="max-w-full mx-auto px-4 sm:px-6 lg:px-8">
        <InstantSearch searchClient={searchClient} indexName="installers">
          <Configure hitsPerPage={4} />
          <div className="flex flex-col">
            <div className="flex flex-row flex-1">
              <div className="w-full px-4 py-8 mx-auto">
                <ConnectedCustomSearchBox setSearchTerm={setSearchTerm} />
                {searchTerm && <Hits hitComponent={Hit} />}
                <div className="mx-auto">
                  {searchTerm && <ConnectedPagination />}
                </div>
              </div>
            </div>
          </div>
        </InstantSearch>
      </div>
    </div>
  );
}
type HitProps = {
  hit: {
    objectID: string;
    name: string;
    rank: number;
  };
};

function Hit(props: HitProps) {
  return (
    <article className="my-8">
      <h1 className="text-2xl font-medium text-gray-800 hover:text-[#15c]">
        <Link
          href={'/solar-installer-reviews/' + props.hit.objectID}
          className="cursor-pointer"
        >
          <Highlight attribute="name" hit={props.hit} />
        </Link>
      </h1>
    </article>
  );
}

export default ReviewSearch;
